import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db9l8e3k13ra8mig95g0";

const services = [
  {
    icon: "ri-layout-grid-line",
    title: "多维分类检索",
    desc: "电影与剧集分区管理，按类型、地区、年代三维度组合筛选，减少无效翻页。",
  },
  {
    icon: "ri-search-line",
    title: "片名精准搜索",
    desc: "支持片名、类型与主演关键词匹配，搜索结果可直接进入详情页查看完整资料。",
  },
  {
    icon: "ri-file-text-line",
    title: "完整影片资料",
    desc: "上映时间、导演、主演、时长与剧情简介集中展示，帮助你在观看前了解作品。",
  },
];

const notices = [
  "本站为影视内容展示演示站点，页面中的片名、评分、演职人员与剧情均为虚构示例，不对应任何真实作品。",
  "本站不提供真实播放、下载、收藏或分享服务，相关按钮仅作界面演示，不会产生实际数据。",
  "页面所有影视封面与剧照均为演示用途的生成图片，版权归原生成方所有，请勿用于商业用途。",
  "如需引用站内文字内容，请注明来源；如发现内容问题，欢迎通过反馈表单与我们联系。",
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "关于我们 - 嫩草影院高清影视内容平台",
    description:
      "了解嫩草影院的内容定位、服务范围与联系方式。我们专注高清影视内容展示，提供分类检索与影片资料浏览体验。",
    keywords: "关于嫩草影院,嫩草影院介绍,影视平台联系,意见反馈",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");

    const form = event.currentTarget;
    const payload = new FormData(form);
    const honeypot = String(payload.get("contact_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    payload.delete("contact_alt");
    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMessage && typeof serverMessage === "string" && serverMessage.length < 200
            ? serverMessage
            : "提交失败，请稍后重试或直接发送邮件给我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请检查网络后重试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        <section className="w-full bg-background-300 border-b border-background-400/60">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <p className="text-[11px] tracking-[0.3em] text-primary-500">ABOUT NEVCAO</p>
            <h1 className="mt-3 text-2xl md:text-4xl font-black text-foreground-950">
              让找片这件事，变得简单一点
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-[15px] leading-8 text-foreground-700">
              嫩草影院是一个面向影视爱好者的内容展示平台。我们不做复杂的社交与推荐算法，而是把片库结构、筛选维度与影片资料做清楚，让你在几秒钟内判断一部作品是否值得看。
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#feedback"
                className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-chat-3-line" />
                提交意见反馈
              </a>
              <a
                href="#notice"
                className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-background-100 hover:bg-background-200 border border-background-400 text-foreground-900 text-sm transition-colors whitespace-nowrap"
              >
                查看使用须知
              </a>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">我们在做什么</h2>
              <div className="mt-4 space-y-4 text-sm leading-8 text-foreground-700">
                <p>
                  嫩草影院的片库以电影与电视剧为主线，覆盖动作、爱情、科幻、悬疑、犯罪、剧情、古装、治愈等常见题材。每一条内容都包含封面、类型、地区、年份、评分与剧情简介，方便快速判断是否符合口味。
                </p>
                <p>
                  分类页提供类型、地区、年代三个维度的组合筛选，并支持按最新、评分、热门三种方式排序；搜索页支持片名、类型与主演关键词匹配。我们相信清晰的信息结构，比花哨的推荐更能节省时间。
                </p>
                <p>
                  需要说明的是，本站为演示性质的内容站，播放、下载、收藏等功能不在范围内，页面中的影片信息也均为虚构示例，用于展示产品形态与视觉设计。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {services.map((service) => (
                  <div
                    key={service.title}
                    className="rounded-lg bg-background-100 border border-background-300/80 p-5"
                  >
                    <span className="w-10 h-10 rounded-md bg-primary-500/15 border border-primary-500/40 flex items-center justify-center text-primary-500 text-lg">
                      <i className={service.icon} />
                    </span>
                    <h3 className="mt-4 text-sm font-semibold text-foreground-950">
                      {service.title}
                    </h3>
                    <p className="mt-2 text-xs leading-6 text-foreground-600">{service.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <aside className="rounded-lg bg-background-300 border border-background-400/70 p-5 md:p-6 h-fit">
              <h2 className="text-sm font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4 text-xs">
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-background-100 border border-background-400/70 text-primary-500">
                    <i className="ri-mail-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500 mb-1">合作与反馈邮箱</span>
                    <span className="text-foreground-900 break-all">
                      hello@nevcao-cinema.demo
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-background-100 border border-background-400/70 text-primary-500">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500 mb-1">内容更新</span>
                    <span className="text-foreground-900">每周一、周四更新片库条目</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-background-100 border border-background-400/70 text-primary-500">
                    <i className="ri-map-pin-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500 mb-1">办公地址</span>
                    <span className="text-foreground-900">演示站点，暂不提供线下接待</span>
                  </span>
                </li>
              </ul>
              <div className="mt-5 pt-4 border-t border-background-400/70">
                <p className="text-[11px] leading-6 text-foreground-500">
                  我们会在收到反馈后的 3 个工作日内回复。若涉及内容下架，请提供具体页面链接。
                </p>
              </div>
            </aside>
          </div>
        </section>

        <section id="notice" className="w-full bg-background-100/60 border-y border-background-300/70">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">使用须知与版权声明</h2>
            <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {notices.map((notice, index) => (
                <li
                  key={notice}
                  className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-300/80 p-4"
                >
                  <span className="font-display text-sm font-semibold text-accent-500 shrink-0 mt-0.5">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="text-xs leading-6 text-foreground-600">{notice}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="feedback" className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">意见与反馈</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                无论是内容纠错、分类建议，还是页面体验上的问题，都欢迎告诉我们。表单提交后我们会收到通知，并在工作日内回复。
              </p>
              <div className="mt-5 rounded-lg bg-background-300 border border-background-400/70 p-4">
                <p className="text-xs leading-6 text-foreground-600">
                  <i className="ri-shield-check-line mr-1.5 text-primary-500" />
                  我们仅将你填写的信息用于处理本次反馈，不会用于其他用途。
                </p>
              </div>
            </div>

            <div className="lg:col-span-3 rounded-lg bg-background-100 border border-background-300/80 p-5 md:p-7">
              {status === "success" ? (
                <div className="py-10 text-center">
                  <span className="w-14 h-14 mx-auto rounded-full bg-accent-500/15 border border-accent-500/50 flex items-center justify-center text-accent-500 text-2xl">
                    <i className="ri-check-line" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">
                    感谢你的反馈
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    我们已经收到你的留言，会在 3 个工作日内回复到你填写的邮箱。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-5 h-10 px-5 rounded-md bg-background-200 hover:bg-background-300 border border-background-400 text-sm text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-xs text-foreground-600 mb-2">
                        你的称呼 <span className="text-primary-500">*</span>
                      </span>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="怎么称呼你"
                        className="w-full h-11 px-3 rounded-md bg-background-200 border border-background-400 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-xs text-foreground-600 mb-2">
                        邮箱 <span className="text-primary-500">*</span>
                      </span>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="name@example.com"
                        className="w-full h-11 px-3 rounded-md bg-background-200 border border-background-400 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="block text-xs text-foreground-600 mb-2">反馈类型</span>
                    <span className="relative block">
                      <select
                        name="topic"
                        defaultValue="内容纠错"
                        className="w-full h-11 pl-3 pr-9 rounded-md bg-background-200 border border-background-400 text-sm text-foreground-950 appearance-none outline-none focus:border-primary-500 cursor-pointer"
                      >
                        <option value="内容纠错">内容纠错</option>
                        <option value="分类建议">分类建议</option>
                        <option value="页面体验">页面体验</option>
                        <option value="合作咨询">合作咨询</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none" />
                    </span>
                  </label>

                  <label className="block">
                    <span className="block text-xs text-foreground-600 mb-2">
                      反馈内容 <span className="text-primary-500">*</span>
                    </span>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你遇到的问题或建议，最多 500 字"
                      className="w-full px-3 py-2.5 rounded-md bg-background-200 border border-background-400 text-sm leading-6 text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors resize-none"
                    />
                  </label>

                  <input
                    type="text"
                    name="contact_alt"
                    className="field-extra"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="text-xs text-accent-500 bg-accent-500/10 border border-accent-500/40 rounded-md px-3 py-2">
                      {formError || "提交失败，请稍后重试。"}
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                    <p className="text-[11px] text-foreground-500">
                      提交即表示同意我们按隐私说明处理相关信息
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="h-11 px-6 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          提交反馈
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}